import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";

import { useDataApi, useWeatherApi } from "@core/apis";
import {
  getRadiatorCurvesBySubstationOptions,
  useWeatherCoordinates,
  type Sensor,
  type SensorDataPerSubstation,
} from "@core/apis/shared";
import { useFilteredSensors } from "@core/apis/shared/hooks";
import { getWeatherOptions } from "@core/apis/Weather";

import { type Resolution } from "../charts/chartConstants";
import { useGetSensorsDataQuery } from "../customQueryHooks";
import {
  combineSupplyAndOutdoorData,
  extractCurvePoints,
  extractRadiatorSupplyTempFromSensorData,
  findRadiatorSupplyTempSensor,
} from "../RadiatorCurve/radiator-curve.utils";

export type RadiatorSensorDataMap = Record<string, SensorDataPerSubstation[]>;

type RadiatorCurveDateRange = {
  start: DateTime;
  end: DateTime;
};

type UseRadiatorCurveDataArgs = {
  substationId?: string;
  networkUid?: string;
  dateRange: RadiatorCurveDateRange;
  sensors?: Sensor[];
  sensorsLoading?: boolean;
  sensorDataByUid?: RadiatorSensorDataMap;
  sensorDataLoading?: boolean;
  sensorTypes?: string[];
  fetchSensors?: boolean;
  fetchSensorData?: boolean;
  includeRadiatorCurves?: boolean;
  resolution?: Resolution;
};

type UseRadiatorCurveDataResult = {
  curvePoints: ReturnType<typeof extractCurvePoints>;
  supplyDataPoints: Array<{ outdoor_temperature: number; supply_temperature: number }>;
  isLoading: boolean;
  radiatorCurvesLoading: boolean;
  weatherLoading: boolean;
  sensors: Sensor[];
  sensorsLoading: boolean;
  sensorDataByUid: RadiatorSensorDataMap;
  sensorDataLoading: boolean;
  radiatorSupplySensor?: Sensor;
  radiatorCurveError: unknown;
  radiatorSupplyTempSeries: Array<{ datetime: string; value: number }>;
  weatherData: Array<{ datetime: string; value: number }>;
  weatherCoordinates?: { lat: number; lon: number };
};

const DEFAULT_SENSOR_TYPES = [
  "radiator_supplytemp_offset",
  "radiator_supplytemp_control_offset",
  "radiator_system_supply_temp",
];

const aggregateMetaDataByResolution = (
  data: Array<{ datetime: string; value: number }>,
  resolution: Resolution
): Array<{ datetime: string; value: number }> => {
  if (resolution === "as_measured" || data.length === 0) {
    return data;
  }

  const map = new Map<string, number[]>();
  for (const point of data) {
    const date = DateTime.fromISO(point.datetime);
    if (!date.isValid || typeof point.value !== "number" || !Number.isFinite(point.value)) continue;

    const key = resolution === "daily" ? date.toFormat("yyyy-MM-dd") : date.startOf("hour").toISO();
    if (!key) continue;

    const bucket = map.get(key) ?? [];
    bucket.push(point.value);
    map.set(key, bucket);
  }

  if (map.size === 0) return [];

  return Array.from(map.entries()).map(([key, values]) => {
    const average = values.reduce((sum, v) => sum + v, 0) / values.length;
    return {
      datetime: key,
      value: Number(average.toFixed(2)),
    };
  });
};

export function useRadiatorCurveData({
  substationId,
  dateRange,
  sensors: providedSensors,
  sensorsLoading: providedSensorsLoading,
  sensorDataByUid: providedSensorData,
  sensorDataLoading: providedSensorDataLoading,
  sensorTypes = DEFAULT_SENSOR_TYPES,
  fetchSensors = true,
  fetchSensorData = true,
  networkUid,
  includeRadiatorCurves = true,
  resolution = "as_measured",
}: UseRadiatorCurveDataArgs): UseRadiatorCurveDataResult {
  const dataApi = useDataApi();
  const weatherApi = useWeatherApi();

  const hasSubstation = Boolean(substationId);

  const shouldFetchSensors = fetchSensors && hasSubstation && !providedSensors;

  const { data: fetchedSensors = [], isLoading: fetchedSensorsLoading } = useFilteredSensors(
    substationId ?? "",
    sensorTypes,
    { enabled: shouldFetchSensors }
  );

  const sensors = providedSensors ?? fetchedSensors;
  const sensorsLoading = providedSensorsLoading ?? fetchedSensorsLoading ?? false;

  const sensorUids = useMemo(() => sensors.map((sensor) => sensor.uid), [sensors]);

  const shouldFetchSensorData =
    fetchSensorData &&
    hasSubstation &&
    sensorUids.length > 0 &&
    !providedSensorData &&
    !providedSensorDataLoading;

  const sensorDateRange = useMemo(
    () => ({
      startDate: dateRange.start,
      endDate: dateRange.end,
    }),
    [dateRange.start, dateRange.end]
  );

  const { data: fetchedSensorData, isLoading: fetchedSensorDataLoading } = useGetSensorsDataQuery(
    substationId ?? "",
    sensorUids,
    sensorDateRange,
    {
      enabled: shouldFetchSensorData,
    }
  );

  const sensorDataByUid: RadiatorSensorDataMap = providedSensorData ?? fetchedSensorData ?? {};
  const sensorDataLoading =
    providedSensorDataLoading ??
    (shouldFetchSensorData ? fetchedSensorDataLoading : false) ??
    false;

  const shouldFetchCurves = includeRadiatorCurves && hasSubstation && !!dataApi;

  const {
    data: radiatorCurves = [],
    isLoading: isRadiatorCurvesLoading,
    error: radiatorCurveError,
  } = useQuery({
    ...getRadiatorCurvesBySubstationOptions(substationId ?? "", dataApi),
    enabled: shouldFetchCurves,
  });

  const { data: weatherCoordinates, isLoading: isWeatherCoordinatesLoading } =
    useWeatherCoordinates(substationId, networkUid);

  const { data: weatherData = [], isLoading: isWeatherLoading } = useQuery({
    ...getWeatherOptions(weatherApi, {
      coordinates: weatherCoordinates,
      startDate: dateRange.start.toISO(),
      endDate: dateRange.end.toISO(),
    }),
    enabled: hasSubstation && !!weatherApi && !!weatherCoordinates,
  });

  const curvePoints = useMemo(() => {
    if (isRadiatorCurvesLoading || !Array.isArray(radiatorCurves)) {
      return [];
    }
    return extractCurvePoints(radiatorCurves);
  }, [radiatorCurves, isRadiatorCurvesLoading]);

  const radiatorSupplySensor = useMemo(
    () => (sensors.length ? findRadiatorSupplyTempSensor(sensors) : undefined),
    [sensors]
  );

  const radiatorSupplyTempData = useMemo(() => {
    if (!radiatorSupplySensor) {
      return [];
    }
    const readings = sensorDataByUid[radiatorSupplySensor.uid] ?? [];
    return extractRadiatorSupplyTempFromSensorData(readings);
  }, [radiatorSupplySensor, sensorDataByUid]);

  const { aggregateRadiatorSupplyTempData, aggregateWeatherData } = useMemo(() => {
    return {
      aggregateRadiatorSupplyTempData: aggregateMetaDataByResolution(
        radiatorSupplyTempData,
        resolution
      ),
      aggregateWeatherData: aggregateMetaDataByResolution(weatherData, resolution),
    };
  }, [radiatorSupplyTempData, weatherData, resolution]);

  const supplyDataPoints = useMemo(() => {
    return combineSupplyAndOutdoorData(aggregateRadiatorSupplyTempData, aggregateWeatherData);
  }, [aggregateRadiatorSupplyTempData, aggregateWeatherData]);

  const isLoading =
    (includeRadiatorCurves ? isRadiatorCurvesLoading : false) ||
    isWeatherCoordinatesLoading ||
    isWeatherLoading ||
    sensorsLoading ||
    sensorDataLoading;

  return {
    curvePoints,
    supplyDataPoints,
    isLoading,
    radiatorCurvesLoading: includeRadiatorCurves ? isRadiatorCurvesLoading : false,
    weatherLoading: isWeatherLoading,
    sensors,
    sensorsLoading,
    sensorDataByUid,
    sensorDataLoading,
    radiatorSupplySensor,
    radiatorCurveError: includeRadiatorCurves ? radiatorCurveError : undefined,
    radiatorSupplyTempSeries: radiatorSupplyTempData,
    weatherData,
    weatherCoordinates,
  };
}
